<template>
  <v-card class="pa-6" flat>
    <v-form ref="form" v-model="valid">
      <!-- Title Section -->
      <h3 class="title mb-6">Edit Landlord</h3>
      
      <!-- Search Section -->
      <v-row>
        <v-col cols="12" md="8">
          <div class="text-subtitle-1 black--text mb-2">Search for landlord to edit</div>
          <Search
            label="Search by name"
            @selectedOID="fetchLandlord" 
            @clearItem="handleClearSearch"
            endpoint="landlord"
            myParams="name"
          />
        </v-col>
      </v-row>

      <!-- Selected Landlord Card -->
      <v-card v-if="selectedLandlordID && !fetchingLandlord" class="mt-4 mb-6" outlined>
        <v-card-text>
          <div class="text-subtitle-2 mb-1">Currently Editing:</div>
          <div class="text-body-1">{{ displayLandlordName }}</div>
          <div class="text-caption">Landlord ID: {{ selectedLandlordID }}</div>
        </v-card-text>
      </v-card>

      <!-- Loading Progress -->
      <v-progress-linear v-if="fetchingLandlord" indeterminate color="secondary" class="mb-4"></v-progress-linear>

      <!-- Landlord Details Form -->
      <div v-if="selectedLandlordID">
        <v-divider class="mb-6"></v-divider>
        
        <!-- Personal Details Section -->
        <h4 class="subtitle-1 mb-4">Personal Details</h4>
        <v-row>
          <v-col cols="12" md="2">
            <v-select
              v-model="title"
              :items="titles"
              label="Title"
              outlined
              dense
              :disabled="fetchingLandlord"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="firstName"
              label="First Name"
              outlined
              dense
              :disabled="fetchingLandlord"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="surname"
              label="Surname"
              outlined
              dense
              :disabled="fetchingLandlord"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="email"
              label="Email"
              outlined
              dense
              type="email"
              :disabled="fetchingLandlord"
              :rules="emailRules"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="mobile"
              label="Mobile Phone"
              outlined
              dense
              :disabled="fetchingLandlord"
              :rules="phoneRules"
            />
          </v-col>
        </v-row>

        <!-- Address Section -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">Address</h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="address1"
              label="Address Line 1"
              outlined
              dense
              :disabled="fetchingLandlord"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="address2"
              label="Address Line 2"
              outlined
              dense
              :disabled="fetchingLandlord"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="address3"
              label="Address Line 3"
              outlined
              dense
              :disabled="fetchingLandlord"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="address4"
              label="Address Line 4"
              outlined
              dense
              :disabled="fetchingLandlord"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="postcode"
              label="Postcode"
              outlined
              dense
              :disabled="fetchingLandlord"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>

        <!-- Bank Details Section (Read-only) -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">
          Bank Details
          <v-chip x-small class="ml-2" color="grey lighten-2">Read Only</v-chip>
        </h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="accountName"
              label="Account Name"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="account"
              label="Account Number"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="sortCode"
              label="Sort Code"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="statementRef"
              label="Statement Reference"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
        </v-row>

        <!-- Management Fee Section -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">Management Fee</h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="fee"
              label="Management Fee Percentage"
              outlined
              dense
              type="number"
              suffix="%"
              :disabled="fetchingLandlord"
              :rules="[rules.required, rules.percentage]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-menu
              v-model="menu1"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedManagementDate"
                  label="Apply From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  :disabled="fetchingLandlord"
                  :rules="[rules.required]"
                  clearable
                  @click:clear="date = null"
                />
              </template>
              <v-date-picker
                v-model="date"
                @input="menu1 = false"
              />
            </v-menu>
          </v-col>
        </v-row>

        <!-- NRL Status Section -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">NRL Status</h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-menu
              v-model="nrlDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedNrlDate"
                  label="NRL Apply From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  :disabled="fetchingLandlord"
                  clearable
                  @click:clear="NrlStatus.ApplyFrom = null"
                />
              </template>
              <v-date-picker
                v-model="NrlStatus.ApplyFrom"
                @input="nrlDateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" md="6">
            <v-switch
              v-model="NrlStatus.NrlApplies"
              label="NRL Applies"
              :disabled="fetchingLandlord"
              class="mt-2"
            />
          </v-col>
        </v-row>
        
        <v-row v-if="NrlStatus.NrlApplies">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="NrlStatus.NrlNumber"
              label="NRL Number"
              outlined
              dense
              :disabled="fetchingLandlord"
              :rules="nrlNumberRules"
            />
          </v-col>
        </v-row>
        
        <!-- Action Buttons -->
        <v-row class="mt-6">
          <v-col cols="12" class="text-right">
            <v-btn
              text
              @click="handleClearSearch"
              :disabled="loading"
              class="mr-3"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="loading"
              :disabled="!valid || fetchingLandlord || (NrlStatus.NrlApplies && !NrlStatus.NrlNumber)"
              @click="saveLandlord"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-8">
        <v-icon size="48" color="grey lighten-2">mdi-account-search</v-icon>
        <p class="text-subtitle-1 grey--text mt-3">Search for a landlord above to start editing</p>
      </div>
    </v-form>

    <!-- Snackbar for notifications -->
    <Snackbar 
      :snackbarDialog="snackbar"
      :snackbarText="snackbarText"
      :timeout="timeout"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
    />
  </v-card>
</template>

<script>
import { amplifyProxy } from '@/services/directApi'
import Search from '@/components/CloudSearch/Search'
import Snackbar from '@/components/Snackbars/TheSnackbar'
import moment from 'moment/src/moment'

export default {
  name: 'edit-landlord',
  components: {
    Search,
    Snackbar
  },
  data() {
    return {
      valid: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      loading: false,
      fetchingLandlord: false,
      menu1: false,
      selectedLandlordID: '',
      title: '',
      firstName: '',
      surname: '',
      email: '',
      mobile: '',
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid',
      ],
      phoneRules: [
        v => !!v || 'Mobile phone is required',
        v => /^[\d\s+()-]+$/.test(v) || 'Phone number must be valid'
      ],
      address1: '',
      address2: '',
      address3: '',
      address4: '',
      postcode: '',
      accountName: '',
      account: '',
      sortCode: '',
      statementRef: '',
      fee: '',
      date: '',
      NrlStatus: {
        ApplyFrom: '',
        NrlApplies: false,
        NrlNumber: ''
      },
      nrlDateMenu: false,
      nrlNumberRules: [
        v => !!v || 'NRL number is required',
      ],
      titles: ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof', 'Rev', 'Other'],
      rules: {
        required: value => !!value || 'Required field',
        percentage: value => {
          if (value === null || value === '') return 'Required field'
          return (value >= 0 && value <= 100) || 'Must be between 0 and 100'
        }
      }
    }
  },
  methods: {
    closeSnackbar() {
      this.snackbar = false
    },

    handleClearSearch() {
      this.selectedLandlordID = ''
      this.clearForm()
    },

    async fetchLandlord(id) {
      this.selectedLandlordID = id
      this.clearForm()
      this.fetchingLandlord = true

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/landlords/${id}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.data

        // Personal details
        this.title = data.Name?.Title || ''
        this.firstName = data.Name?.Forename || ''
        this.surname = data.Name?.Surname || ''
        this.email = data.Email || ''
        this.mobile = data.Mobile || ''

        // Address
        this.address1 = data.Address?.Address1 || ''
        this.address2 = data.Address?.Address2 || ''
        this.address3 = data.Address?.Address3 || ''
        this.address4 = data.Address?.Address4 || ''
        this.postcode = data.Address?.Postcode || ''

        // Bank details
        this.accountName = data.BankAccount?.AccountName || ''
        this.account = data.BankAccount?.AccountNumber || ''
        this.sortCode = data.BankAccount?.SortCode || ''
        this.statementRef = data.BankAccount?.LegacyBacsReference || ''

        // Management fee
        this.fee = data.ManagementFeePercent || ''
        this.date = data.ManagementFeeApplyFrom ? data.ManagementFeeApplyFrom.substr(0, 10) : ''

        // NRL Status
        this.NrlStatus.ApplyFrom = data.NrlStatus?.ApplyFrom ? data.NrlStatus.ApplyFrom.substr(0, 10) : ''
        this.NrlStatus.NrlApplies = data.NrlStatus?.NrlApplies || false
        this.NrlStatus.NrlNumber = data.NrlStatus?.NrlNumber || ''
      } catch (error) {
        console.error('Error fetching landlord:', error)
        this.snackbar = true
        this.snackbarText = 'Error fetching landlord details: ' + (error.response?.data?.message || error.message)
        this.snackbarColor = 'red'
      } finally {
        this.fetchingLandlord = false
      }
    },

    clearForm() {
      this.title = ''
      this.firstName = ''
      this.surname = ''
      this.email = ''
      this.mobile = ''
      this.address1 = ''
      this.address2 = ''
      this.address3 = ''
      this.address4 = ''
      this.postcode = ''
      this.accountName = ''
      this.account = ''
      this.sortCode = ''
      this.statementRef = ''
      this.fee = ''
      this.date = ''
      this.NrlStatus.ApplyFrom = ''
      this.NrlStatus.NrlApplies = false
      this.NrlStatus.NrlNumber = ''
    },

    async saveLandlord() {
      this.loading = true

      const landlordObject = {
        Name: {
          Title: this.title,
          Forename: this.firstName,
          Surname: this.surname
        },
        Email: this.email,
        Mobile: this.mobile,
        BankAccount: {
          AccountName: this.accountName,
          AccountNumber: this.account,
          SortCode: this.sortCode,
          LegacyBacsReference: this.statementRef
        },
        ManagementFeePercent: this.fee,
        DateManagementFee: `${this.date}`,
        Address: {
          Address1: this.address1,
          Address2: this.address2,
          Address3: this.address3,
          Address4: this.address4,
          Postcode: this.postcode
        },
        NrlStatus: {
          ApplyFrom: `${this.NrlStatus.ApplyFrom}T00:01:00`,
          NrlApplies: this.NrlStatus.NrlApplies,
          NrlNumber: this.NrlStatus.NrlNumber,
        }
      }


      try {
        const endpoint = `/${this.$store.state.user.ClientName}/landlords/${this.selectedLandlordID}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        await amplifyProxy.patch(endpoint, landlordObject, options)

        this.snackbar = true
        this.snackbarText = 'Landlord update successful'
        this.snackbarColor = 'green'
        await this.fetchLandlord(this.selectedLandlordID)
      } catch (error) {
        this.snackbar = true
        this.snackbarText = 'ERROR: ' + (error.response?.data || error.message)
        this.snackbarColor = 'red'
        await this.fetchLandlord(this.selectedLandlordID)
      } finally {
        this.loading = false
      }
    }
  },
  computed: {
    displayLandlordName() {
      if (!this.firstName && !this.surname) return 'No landlord selected'
      return `${this.title} ${this.firstName} ${this.surname}`.trim()
    },
    formattedNrlDate() {
      return this.NrlStatus.ApplyFrom ? moment(this.NrlStatus.ApplyFrom).format('DD/MM/YYYY') : ''
    },
    formattedManagementDate() {
      return this.date ? moment(this.date).format('DD/MM/YYYY') : ''
    }
  }
}
</script>